import { HttpErrorResponse } from '@angular/common/http';

const WRAPPER_KEYS = ['ngOriginalError', 'rejection', 'originalError'] as const;
const MAX_UNWRAP_DEPTH = 5;

/**
 * Turns an Angular `HttpErrorResponse` into a real `Error`.
 *
 * `HttpErrorResponse` implements the Error shape and does not extend `Error`.
 * Sentry's `captureException` then records "Object captured as exception with
 * keys: ..." and drops the status and URL (SOLO-TENANT-PORTAL-WEB-23, AB#20319).
 * Zone also wraps the value in `ngOriginalError`, `rejection`, or `originalError`
 * before the Angular error handler sees it.
 */
export function toReportableError(value: unknown): unknown {
  const httpError = findHttpError(value, 0);
  if (!httpError) {
    return value;
  }

  const status = httpError.status;
  const statusText = httpError.statusText || '';
  const url = httpError.url || '';
  const reportable = new Error(
    `Http failure response for ${url}: ${status} ${statusText}`.trim()
  );
  reportable.name = 'HttpErrorResponse';
  return reportable;
}

function findHttpError(value: unknown, depth: number): HttpErrorLike | null {
  if (depth > MAX_UNWRAP_DEPTH || value == null || typeof value !== 'object') {
    return null;
  }

  if (isHttpError(value)) {
    return value;
  }

  const record = value as Record<string, unknown>;
  for (const key of WRAPPER_KEYS) {
    if (key in record) {
      const nested = findHttpError(record[key], depth + 1);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
}

interface HttpErrorLike {
  status: number;
  statusText: string;
  url: string | null;
}

function isHttpError(value: object): value is HttpErrorLike {
  if (value instanceof HttpErrorResponse) {
    return true;
  }

  const candidate = value as { name?: unknown; status?: unknown; statusText?: unknown; url?: unknown };
  return candidate.name === 'HttpErrorResponse'
    && typeof candidate.status === 'number'
    && (candidate.statusText == null || typeof candidate.statusText === 'string')
    && (candidate.url == null || typeof candidate.url === 'string');
}
